/* eslint-disable default-param-last */
// (Redux reducers take `(state = initial, action)` by contract; the house
// convention is a file-level disable, matching gameReducers.js.)
import {
  SOCIAL_PENDING_REQUESTS_SET,
  SOCIAL_PRESENCE_MERGE,
  SOCIAL_DM_UNREAD_SET,
} from '../types/socialTypes';

// Social slice: the follow-request badge count, a presence cache, and the DM
// unread counters.
//
// Presence MERGES rather than replaces. Each poll only asks about the ids
// currently visible in the viewport, so a replace would blank out everyone
// who happened to scroll off screen between polls and make dots flicker.
//
// `dmUnread` holds BOTH numbers the API returns, but the badge renders
// `conversations`, not `messages`. A badge counting individual messages turns
// one chatty friend into a "47" that says nothing about how many threads
// actually need attention - the coalescing the backend does per-conversation
// only pays off if the UI honours it.
const initialState = {
  pendingRequests: 0,
  presence: {},
  dmUnread: { conversations: 0, messages: 0 },
};

export const socialReducer = (state = initialState, action) => {
  switch (action.type) {
    case SOCIAL_PENDING_REQUESTS_SET:
      // Identity-stable when the count has not actually changed. Returning a
      // fresh object for an unchanged value re-renders every useSelector
      // subscriber (the sidebar nav and the bottom nav both subscribe), and
      // any list re-rendered underneath a Playwright click detaches the node
      // mid-action - which surfaces as "element was detached from the DOM,
      // retrying" rather than as anything that looks like a render bug.
      if (state.pendingRequests === action.payload) return state;
      return { ...state, pendingRequests: action.payload };
    case SOCIAL_PRESENCE_MERGE: {
      // Same reasoning as above: a poll that returns identical presence (the
      // common case - nobody's status changed in the last 45s) must not
      // produce a new object, or every poll re-renders every subscriber.
      const incoming = action.payload || {};
      const changed = Object.keys(incoming).some((id) => {
        const prev = state.presence[id];
        return !prev
          || prev.is_online !== incoming[id].is_online
          || prev.last_seen_at !== incoming[id].last_seen_at;
      });
      if (!changed) return state;
      return { ...state, presence: { ...state.presence, ...incoming } };
    }
    case SOCIAL_DM_UNREAD_SET: {
      // Identity-stable, for the same reason as the two cases above: this is
      // refetched on every WS reconnect and after every thread open, and the
      // navbar badge plus the sidebar nav row both subscribe. Returning a
      // fresh object for an unchanged count re-renders them (and anything
      // rendered under a Playwright click detaches mid-action, which surfaces
      // as "element was detached from the DOM, retrying" rather than as
      // anything resembling a render bug).
      const conversations = Number(action.payload?.conversations) || 0;
      const messages = Number(action.payload?.messages) || 0;
      if (state.dmUnread.conversations === conversations && state.dmUnread.messages === messages) {
        return state;
      }
      return { ...state, dmUnread: { conversations, messages } };
    }
    default:
      return state;
  }
};

export default socialReducer;
